import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

/**
 * The small rendered preview of each reproduction card (what gets marked),
 * as object URLs by reproduction id: ``undefined`` while loading, ``""``
 * when the request failed.
 *
 * ``load(rows)`` requests one preview per row; each card shows its own as
 * soon as it returns. A preview of an earlier load, or one arriving after
 * the page closed, is dropped instead of shown. Object URLs are revoked
 * when replaced and on unmount.
 */
export default function useReproductionThumbnails() {
    const [thumbnails, setThumbnails] = useState({});
    const urlsRef = useRef({});
    // Counts loads: a thumbnail of an earlier load is dropped.
    const loadRef = useRef(0);

    const load = useCallback((rows) => {
        loadRef.current += 1;
        const load = loadRef.current;
        (rows || []).forEach(async (row) => {
            let url = "";
            try {
                const response = await axios.post(
                    "api/reproduction/rendered_preview",
                    { reproduction_id: row.id, long_edge: 240 },
                    { responseType: "blob" },
                );
                url = URL.createObjectURL(response.data);
            } catch (error) {
                url = "";
            }
            if (loadRef.current !== load) {
                if (url) URL.revokeObjectURL(url);
                return;
            }
            const previous = urlsRef.current[row.id];
            if (previous) URL.revokeObjectURL(previous);
            urlsRef.current[row.id] = url;
            setThumbnails((old) => ({ ...old, [row.id]: url }));
        });
    }, []);

    useEffect(() => () => {
        loadRef.current += 1;
        Object.values(urlsRef.current).forEach((url) => { if (url) URL.revokeObjectURL(url); });
    }, []);

    return { thumbnails, load };
}
